import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { ReactNode } from 'react';

// renderMarkdownInline — tiny inline **bold** → <strong> renderer. Safe
// against XSS (operates on already-parsed string · no innerHTML). Kept
// minimal to avoid a react-markdown dep just for one Terms section. Add
// other inline tokens (italic, code) here if Privacy/Terms ever need them.
function renderMarkdownInline(text: string): ReactNode {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((chunk, i) => {
    if (chunk.startsWith('**') && chunk.endsWith('**')) {
      return <strong key={i}>{chunk.slice(2, -2)}</strong>;
    }
    return <span key={i}>{chunk}</span>;
  });
}

export default function LegalTerms() {
  const { t } = useTranslation();
  return (
    <article className="max-w-prose mx-auto px-8 py-16 prose-fyd">
      <span className="section-eyebrow">{t('legal.eyebrow')}</span>
      <h1 className="font-serif text-4xl font-medium mb-2">{t('legal.terms.title')}</h1>
      <p className="font-mono text-[11px] tracking-meta uppercase text-ink-3 mb-12">
        {t('legal.lastUpdated')} 2026-05-02
      </p>

      <section className="space-y-8 text-base leading-relaxed text-ink">
        <div>
          <h2 className="font-serif text-2xl font-medium mb-3">1. {t('legal.terms.h1')}</h2>
          <p className="text-ink-2">{t('legal.terms.p1')}</p>
        </div>
        <div>
          <h2 className="font-serif text-2xl font-medium mb-3">2. {t('legal.terms.h2')}</h2>
          <p className="text-ink-2">{t('legal.terms.p2')}</p>
        </div>
        <div>
          <h2 className="font-serif text-2xl font-medium mb-3">3. {t('legal.terms.h3')}</h2>
          <p className="text-ink-2">{t('legal.terms.p3')}</p>
        </div>
        <div data-testid="legal-terms-refund-section">
          <h2 className="font-serif text-2xl font-medium mb-3">4. {t('legal.terms.h4')}</h2>
          <div className="space-y-3 text-ink-2">
            <p>{renderMarkdownInline(t('legal.terms.p4_1'))}</p>
            <p>{renderMarkdownInline(t('legal.terms.p4_2'))}</p>
            <p>{renderMarkdownInline(t('legal.terms.p4_3'))}</p>
          </div>
        </div>
        <div>
          <h2 className="font-serif text-2xl font-medium mb-3">5. {t('legal.terms.h5')}</h2>
          <p className="text-ink-2">{t('legal.terms.p5')}</p>
        </div>
        <div>
          <h2 className="font-serif text-2xl font-medium mb-3">6. {t('legal.terms.h6')}</h2>
          <p className="text-ink-2">{t('legal.terms.p6')}</p>
        </div>
      </section>

      <p className="text-sm text-ink-3 mt-12">
        {t('legal.contact')}{' '}
        <a href="mailto:support@fyd.dev" className="text-ink border-b border-current pb-px">
          support@fyd.dev
        </a>
      </p>
      <p className="text-sm text-ink-3 mt-4">
        <Link to="/legal/privacy" className="text-ink border-b border-current pb-px">
          {t('legal.privacy.title')}
        </Link>
      </p>
    </article>
  );
}
